import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { formatDistanceToNow } from 'date-fns';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useCompletedActionItemsQuery } from '../../../../queries/ActionItem/useCompletedActionItemsQuery';
import { Checkbox } from '../../../components-v2/Checkbox';
import { Skeleton } from '../../../components-v2/Skeleton';
import { THEME } from '../../../components-v2/Theme/theme';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { getActionItemTitle } from '../utils/actionItemText';
import { ContactChips } from './ContactChips';

interface IProps {
	assignedTo: string | undefined;
	search: string;
	contactId?: string | null;
	impersonationContext?: Api.IImpersonationContext;
	onUncomplete: (item: Api.IActionItem) => void;
	onOpen: (item: Api.IActionItem) => void;
}

export const CompletedList: React.FC<IProps> = ({
	assignedTo,
	search,
	contactId,
	impersonationContext,
	onUncomplete,
	onOpen,
}) => {
	const query = useCompletedActionItemsQuery({ assignedTo, search, contactId, impersonationContext });
	const items = React.useMemo(() => query.data?.pages.flatMap(page => page.values ?? []) ?? [], [query.data]);

	if (query.isPending) {
		return (
			<div className={css(styleSheet.list)}>
				{[0, 1, 2].map(index => (
					<Skeleton key={index} styles={[styleSheet.skeleton]} />
				))}
			</div>
		);
	}

	if (items.length === 0) {
		return <div className={css(styleSheet.empty)}>No completed action items yet.</div>;
	}

	return (
		<div className={css(styleSheet.list)} data-testid='completed-list'>
			<div className={css(styleSheet.card)}>
				{items.map(item => {
					const title = getActionItemTitle(item);
					const completedAt = item.completionDate ? new Date(item.completionDate) : null;
					return (
						<div key={item.id} className={css(styleSheet.row)} onClick={() => onOpen(item)}>
							<div onClick={event => event.stopPropagation()}>
								<Checkbox
									size='lg'
									checked
									aria-label={`Reopen ${title}`}
									onCheckedChange={checked => {
										if (!checked) {
											onUncomplete(item);
										}
									}}
								/>
							</div>
							<div className={css(styleSheet.text)}>
								<span className={css(styleSheet.title)}>{title}</span>
								<span className={css(styleSheet.meta)}>
									{completedAt ? `Completed ${formatDistanceToNow(completedAt, { addSuffix: true })}` : 'Completed'}
								</span>
							</div>
							<ContactChips item={item} />
						</div>
					);
				})}
			</div>
			{query.hasNextPage && !query.isFetchingNextPage ? (
				<Waypoint bottomOffset='-200px' onEnter={() => query.fetchNextPage()} />
			) : null}
			{query.isFetchingNextPage ? (
				<div className={css(styleSheet.loadingMore)}>
					<LoadingSpinner type='small' />
				</div>
			) : null}
		</div>
	);
};

const styleSheet = StyleSheet.create({
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	skeleton: {
		height: 44,
		borderRadius: THEME.common.borderRadius,
	},
	empty: {
		padding: THEME.spacing.xxxl,
		textAlign: 'center',
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		overflow: 'hidden',
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		minHeight: 52,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.md}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		cursor: 'pointer',
		opacity: 0.75,
		transition: 'opacity 120ms ease-out',
		':last-child': {
			borderBottom: 'none',
		},
		':hover': {
			backgroundColor: THEME.colors.hover.light,
			opacity: 1,
		},
	},
	text: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minWidth: 0,
		gap: 2,
	},
	title: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
		textDecoration: 'line-through',
		...THEME.common.truncate,
	},
	meta: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	loadingMore: {
		display: 'flex',
		justifyContent: 'center',
		padding: THEME.spacing.md,
	},
});
